import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight, Check } from "lucide-react";

const MAPS_URL = "https://www.google.com/maps?q=19.1901472,72.8370545";
const MAPS_EMBED = "https://www.google.com/maps?q=19.1901472,72.8370545&z=16&output=embed";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    variants={fadeUp}
    custom={delay}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

const contactItems = [
  { icon: Mail, label: "Email", value: "info@devkiielectricals.in", href: "mailto:info@devkiielectricals.in", testId: "contact-email" },
  { icon: Phone, label: "Phone", value: "+91 7718927246", href: "tel:+917718927246", testId: "contact-phone" },
  { icon: MapPin, label: "Office", value: "Devkii Electricals Office, Mumbai, Maharashtra", href: MAPS_URL, testId: "contact-office" },
];

const inputClass =
  "w-full border border-ink/15 bg-transparent px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-brand";

const labelClass = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50";

const initialForm = { name: "", company: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page" className="bg-paper text-ink">
      {/* 1 — Hero */}
      <section data-testid="contact-hero" className="mx-auto w-full max-w-[1400px] px-6 pb-14 pt-32 sm:px-10 lg:px-16 lg:pb-16 lg:pt-44">
        <motion.div variants={fadeUp} custom={0} initial="hidden" animate="visible">
          <p className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand" data-testid="contact-eyebrow">
            <span className="inline-block h-2 w-2 bg-brand" />
            Contact Us
          </p>
          <h1 className="font-heading text-5xl font-black tracking-tighter sm:text-6xl lg:text-7xl" data-testid="contact-heading">
            Let&rsquo;s Connect
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60 sm:text-lg">
            Have a requirement, enquiry, or simply want to know more about Devkii
            Electricals? We would be happy to hear from you.
          </p>
        </motion.div>
      </section>

      {/* 2 — Contact details + Map */}
      <section className="border-t border-ink/10">
        <div className="mx-auto grid w-full max-w-[1400px] gap-14 px-6 py-16 sm:px-10 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-24">
          <Reveal className="lg:col-span-5">
            <h2 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl" data-testid="get-in-touch">
              Get in Touch
            </h2>
            <div className="mt-8 divide-y divide-ink/10 border-y border-ink/10">
              {contactItems.map((item) => (
                <a
                  key={item.testId}
                  href={item.href}
                  target={item.testId === "contact-office" ? "_blank" : undefined}
                  rel={item.testId === "contact-office" ? "noopener noreferrer" : undefined}
                  data-testid={item.testId}
                  className="group flex items-center gap-5 py-6"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-ink/15 transition-colors duration-300 group-hover:border-brand">
                    <item.icon size={16} className="text-brand" />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-ink/40">
                      {item.label}
                    </span>
                    <span className="mt-1 block font-heading text-lg font-bold tracking-tight transition-colors duration-300 group-hover:text-brand">
                      {item.value}
                    </span>
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-10" data-testid="contact-addresses">
              <div data-testid="contact-office-address">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-brand">Registered Office</p>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink/55">
                  Unit No. B/26-F, Malad Industrial Estate, Kanchapada, Ramchandra
                  Extension Road, Malad West, Mumbai 400064, Maharashtra, India
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative min-h-[380px] overflow-hidden rounded-md border border-ink/10 lg:h-full" data-testid="map-embed">
              <iframe
                title="Devkii Electricals Office location map"
                src={MAPS_EMBED}
                className="absolute inset-0 h-full w-full [filter:grayscale(0.85)_contrast(1.02)]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <span className="absolute left-4 top-4 bg-ink px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                Devkii Electricals Office
              </span>
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-link"
              className="group mt-5 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:text-brand"
            >
              View on Google Maps
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* 3 — Enquiry form */}
      <section className="border-t border-ink/10 bg-white" data-testid="enquiry-section">
        <div className="mx-auto grid w-full max-w-[1400px] gap-12 px-6 py-16 sm:px-10 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-24">
          <Reveal className="lg:col-span-4">
            <h2 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl" data-testid="enquiry-heading">
              Send Us an Enquiry
            </h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-ink/60">
              Tell us what you are looking for and our team will get back to you.
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/40">
              Fields marked * are required
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-8">
            {status === "success" ? (
              <div className="flex h-full flex-col items-start justify-center border border-ink/10 p-10 lg:p-14" data-testid="form-success">
                <span className="flex h-12 w-12 items-center justify-center bg-brand text-white">
                  <Check size={22} />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight">Enquiry received.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                  Thank you for reaching out to Devkii Electricals. Your enquiry has been
                  received and our team will get back to you.
                </p>
                <button
                  onClick={() => { setForm(initialForm); setStatus("idle"); }}
                  data-testid="form-send-another"
                  className="mt-8 border border-ink/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-brand hover:text-brand"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid gap-6 sm:grid-cols-2" data-testid="enquiry-form">
                <div>
                  <label htmlFor="enquiry-name" className={labelClass}>Full Name *</label>
                  <input id="enquiry-name" data-testid="input-name" required value={form.name} onChange={set("name")} className={inputClass} placeholder="Your full name" />
                </div>
                <div>
                  <label htmlFor="enquiry-company" className={labelClass}>Company Name</label>
                  <input id="enquiry-company" data-testid="input-company" value={form.company} onChange={set("company")} className={inputClass} placeholder="Your company" />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className={labelClass}>Email Address *</label>
                  <input id="enquiry-email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className={inputClass} placeholder="you@company.com" />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className={labelClass}>Phone Number *</label>
                  <input id="enquiry-phone" data-testid="input-phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+91" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-subject" className={labelClass}>Subject</label>
                  <input id="enquiry-subject" data-testid="input-subject" value={form.subject} onChange={set("subject")} className={inputClass} placeholder="Tooling, OEM or private-label enquiry" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className={labelClass}>Message *</label>
                  <textarea id="enquiry-message" data-testid="input-message" required rows={5} value={form.message} onChange={set("message")} className={`${inputClass} resize-none`} placeholder="Tell us about your requirement" />
                </div>
                {status === "error" && (
                  <p className="text-sm text-brand sm:col-span-2" data-testid="form-error">
                    Something went wrong. Please try again or write to us at info@devkiielectricals.in.
                  </p>
                )}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="submit-enquiry"
                    className="bg-brand px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
                  >
                    {status === "sending" ? "Submitting…" : "Submit Enquiry"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>

    </main>
  );
};
